'use client'

import { cn } from '@brew/ui/cn'
import { Fragment, type ReactNode } from 'react'
import type { LucideIcon } from './icons'

/**
 * Floating "lift" cards for dialog heroes — the homepage's product cards
 * on the sky. Light-fixed in both themes: they are illustrations over the
 * orange backdrop, like the marketing scenes.
 */

const LIFT_CLASS =
  'rounded-[14px] bg-white/[0.96] text-[#302f2c] shadow-[0_0_0_1px_rgb(255_255_255/0.65),0_14px_34px_-12px_rgb(120_48_0/0.38),0_2px_6px_rgb(120_48_0/0.1)]'

export function HeroLift({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return <div className={cn(LIFT_CLASS, className)}>{children}</div>
}

export type HeroTone = 'orange' | 'ink' | 'green' | 'red' | 'violet' | 'blue'

/** Hero tile tones, for hero visuals composed outside these cards. */
export const HERO_TILE_TONE_CLASS: Record<HeroTone, string> = {
  orange: 'bg-[#fff5ee] text-[#d74b00]',
  ink: 'bg-[#f7f7f5] text-[#302f2c]',
  green: 'bg-[#ecf8f0] text-[#1d7a43]',
  red: 'bg-[#fff1f0] text-[#b4232c]',
  violet: 'bg-[#f3eeff] text-[#6d3fd6]',
  blue: 'bg-[#eef4ff] text-[#2f5bd3]',
}

export function HeroIconTile({
  icon: Icon,
  tone = 'orange',
  className,
}: {
  icon: LucideIcon
  tone?: HeroTone
  className?: string
}) {
  return (
    <span
      className={cn(
        'flex size-[30px] shrink-0 items-center justify-center rounded-[9px]',
        HERO_TILE_TONE_CLASS[tone],
        className
      )}
    >
      <Icon className="size-4" strokeWidth={2} aria-hidden="true" />
    </span>
  )
}

export type HeroBadgeTone = 'neutral' | 'orange' | 'green' | 'red' | 'amber'

const BADGE_TONE: Record<HeroBadgeTone, string> = {
  neutral: 'bg-[#f7f7f5] text-[#56554f] ring-[#e6e5e1]',
  orange: 'bg-[#fff5ee] text-[#d74b00] ring-[#ffd9c2]',
  green: 'bg-[#ecf8f0] text-[#1d7a43] ring-[#cdebd8]',
  red: 'bg-[#fff1f0] text-[#b4232c] ring-[#f6cfcf]',
  amber: 'bg-[#fff6e5] text-[#9a5b00] ring-[#f3ddb0]',
}

export function HeroBadge({
  tone = 'neutral',
  children,
}: {
  tone?: HeroBadgeTone
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full px-[9px] font-medium text-xs ring-1 ring-inset',
        BADGE_TONE[tone]
      )}
    >
      {children}
    </span>
  )
}

/** One flow node: icon (or provider mark), a name and a caption. */
export function HeroNode({
  icon,
  tone,
  mark,
  name,
  caption,
  badge,
  className,
}: {
  icon?: LucideIcon
  tone?: HeroTone
  /** A provider logo or custom glyph in place of the icon tile. */
  mark?: ReactNode
  name: ReactNode
  caption?: ReactNode
  badge?: ReactNode
  className?: string
}) {
  return (
    <HeroLift
      className={cn(
        'flex min-w-0 shrink-0 items-center gap-2.5 py-[11px] pr-3.5 pl-[11px]',
        className
      )}
    >
      {mark ? (
        <span className="flex size-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[#f7f7f5]">
          {mark}
        </span>
      ) : null}
      {!mark && icon ? <HeroIconTile icon={icon} tone={tone} /> : null}
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-semibold text-dialog-meta leading-[18px]">
          {name}
        </span>
        {caption ? (
          <span className="truncate text-[#82807c] text-caption leading-[15px]">
            {caption}
          </span>
        ) : null}
        {badge ? <span className="pt-1">{badge}</span> : null}
      </span>
    </HeroLift>
  )
}

/** The dashed edge between hero nodes, ending in a dot like the canvas. */
export function HeroConnector({ width = 28 }: { width?: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-2.5 shrink-0 items-center"
      style={{ width }}
    >
      <span className="h-0 min-w-0 flex-1 border-white/95 border-t-2 border-dashed" />
      <span className="size-1.5 shrink-0 rounded-full bg-white" />
    </span>
  )
}

/** Nodes joined left → right; `more` ends the row with a "+N" pill. */
export function HeroFlow({
  children,
  more,
}: {
  children: Array<ReactNode>
  more?: ReactNode
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {children.map((child, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static decorative row
        <Fragment key={index}>
          {index > 0 ? <HeroConnector /> : null}
          {child}
        </Fragment>
      ))}
      {more ? (
        <>
          <HeroConnector width={20} />
          <HeroLift className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 font-semibold text-[#56554f] text-xs">
            {more}
          </HeroLift>
        </>
      ) : null}
    </div>
  )
}

/** A titled card with key/value rows (domain records, contracts, totals). */
export function HeroInfoCard({
  icon,
  tone,
  title,
  caption,
  rows = [],
  className,
}: {
  icon: LucideIcon
  tone?: HeroTone
  title: ReactNode
  caption?: ReactNode
  rows?: ReadonlyArray<{ label: ReactNode; value: ReactNode }>
  className?: string
}) {
  return (
    <HeroLift className={cn('flex w-[280px] flex-col', className)}>
      <div className="flex items-center gap-2.5 px-3.5 py-3">
        <HeroIconTile icon={icon} tone={tone} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold text-dialog-meta">
            {title}
          </span>
          {caption ? (
            <span className="truncate text-[#82807c] text-caption">
              {caption}
            </span>
          ) : null}
        </div>
      </div>
      {rows.map((row, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative rows
          key={index}
          className="flex items-center justify-between gap-3 border-[#eeede9] border-t px-3.5 py-2 text-xs"
        >
          <span className="truncate text-[#56554f]">{row.label}</span>
          <span className="shrink-0">{row.value}</span>
        </div>
      ))}
    </HeroLift>
  )
}

/** A labelled meter (credits, sends, plan usage). */
export function HeroMeter({
  label,
  value,
  max,
  caption,
}: {
  label: ReactNode
  value: number
  max: number
  caption?: ReactNode
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <HeroLift className="flex w-[248px] flex-col gap-2.5 p-4">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-[#82807c]">{label}</span>
        <span className="font-mono tabular-nums">
          {value.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#f0eeea]">
        <div
          className="h-2 rounded-full bg-[linear-gradient(90deg,#ff772d,#d74b00)]"
          style={{ width: `${pct}%` }}
        />
      </div>
      {caption ? (
        <span className="text-[#5c5b57] text-xs">{caption}</span>
      ) : null}
    </HeroLift>
  )
}

/** Avatar chips (people, contacts, audiences). */
export function HeroChips({
  items,
  className,
}: {
  items: ReadonlyArray<string>
  className?: string
}) {
  return (
    <HeroLift className={cn('flex w-[300px] flex-wrap gap-1.5 p-3', className)}>
      {items.map((item) => (
        <span
          key={item}
          className="inline-flex h-[26px] items-center gap-1.5 rounded-full bg-[#f7f7f5] pr-2.5 pl-1 text-xs ring-1 ring-[#e6e5e1] ring-inset"
        >
          <span className="flex size-[18px] items-center justify-center rounded-full bg-[linear-gradient(135deg,#ffc089,#ff772d)] font-bold text-dialog-micro text-white uppercase">
            {item.slice(0, 1)}
          </span>
          <span className="max-w-[180px] truncate">{item}</span>
        </span>
      ))}
    </HeroLift>
  )
}

/** A dark terminal card (API calls, payload samples). */
export function HeroCode({
  code,
  className,
}: {
  code: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex w-[340px] flex-col overflow-hidden rounded-[14px] bg-[#1e1a17] shadow-[0_14px_34px_-12px_rgb(80_30_0/0.55),0_0_0_1px_rgb(255_255_255/0.08)]',
        className
      )}
    >
      <div className="flex gap-1.5 px-3 py-2.5">
        <span className="size-[9px] rounded-full bg-[#ff5f57]" />
        <span className="size-[9px] rounded-full bg-[#febc2e]" />
        <span className="size-[9px] rounded-full bg-[#28c840]" />
      </div>
      <pre className="overflow-hidden px-3.5 pb-3.5 font-mono text-[#f4e9df] text-caption leading-[18px]">
        {code}
      </pre>
    </div>
  )
}

/** Three big numbers (validation, invites, run totals). */
export function HeroStats({
  items,
}: {
  items: ReadonlyArray<{
    value: ReactNode
    label: ReactNode
    tone?: 'ink' | 'green' | 'amber' | 'red'
  }>
}) {
  return (
    <div className="flex gap-2.5">
      {items.map((item, index) => (
        <HeroLift
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative tiles
          key={index}
          className="flex w-24 flex-col gap-0.5 px-4 py-3"
        >
          <span
            className={cn(
              'font-semibold text-2xl tabular-nums tracking-[-0.02em]',
              {
                'text-[#1d7a43]': item.tone === 'green',
                'text-[#9a5b00]': item.tone === 'amber',
                'text-[#b4232c]': item.tone === 'red',
              }
            )}
          >
            {item.value}
          </span>
          <span className="text-[#82807c] text-caption">{item.label}</span>
        </HeroLift>
      ))}
    </div>
  )
}

/** Date tile + time card (scheduling). */
export function HeroCalendar({
  month,
  day,
  time,
  caption,
}: {
  month: string
  day: string
  time: string
  caption?: string
}) {
  return (
    <div className="flex items-center gap-2.5">
      <HeroLift className="flex w-[78px] flex-col overflow-hidden">
        <span className="bg-[linear-gradient(180deg,#ff772d,#d74b00)] p-1.5 text-center font-bold text-dialog-micro text-white uppercase tracking-dialog-kicker">
          {month}
        </span>
        <span className="pt-1.5 pb-2.5 text-center font-semibold text-dialog-figure leading-8">
          {day}
        </span>
      </HeroLift>
      <HeroLift className="flex flex-col gap-0.5 px-4 py-3">
        <span className="font-semibold text-xl">{time}</span>
        {caption ? (
          <span className="text-[#82807c] text-caption">{caption}</span>
        ) : null}
      </HeroLift>
    </div>
  )
}

/** Score ring + summary badges (audits). */
export function HeroScore({
  score,
  label,
  badges,
}: {
  score: number
  label: ReactNode
  badges?: ReactNode
}) {
  const color = score >= 80 ? '#2baa5e' : '#ff772d'
  return (
    <HeroLift className="flex w-[300px] items-center gap-3.5 px-4 py-3.5">
      <span
        className="flex size-14 shrink-0 items-center justify-center rounded-full"
        style={{
          background: `conic-gradient(${color} ${score * 3.6}deg, #f0eeea 0deg)`,
        }}
      >
        <span className="flex size-11 items-center justify-center rounded-full bg-white font-semibold text-dialog-lead tabular-nums">
          {score}
        </span>
      </span>
      <span className="flex min-w-0 flex-col gap-1.5">
        <span className="font-semibold text-dialog-meta">{label}</span>
        {badges ? (
          <span className="flex flex-wrap gap-1.5">{badges}</span>
        ) : null}
      </span>
    </HeroLift>
  )
}

/** Image tiles, gently fanned (uploads, logos, assets). */
export function HeroTiles({ children }: { children: Array<ReactNode> }) {
  const rotations = ['-rotate-6', 'rotate-2', 'rotate-[7deg]', '-rotate-3']
  return (
    <div className="flex gap-2.5">
      {children.map((child, index) => (
        <HeroLift
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative tiles
          key={index}
          className={cn(
            'flex size-[78px] items-center justify-center overflow-hidden p-1',
            rotations[index % rotations.length]
          )}
        >
          {child}
        </HeroLift>
      ))}
    </div>
  )
}
